import React, { useState, useEffect, useRef } from 'react';
import { ListGroup } from 'react-bootstrap';
import './WorkflowAutocomplete.css';
import { WORKFLOW_FUNCTIONS } from './workflowFunctions';

/**
 * Runbook variables - runtime placeholders available in workflow expressions
 */
const RUNBOOK_VARIABLES = [
  {
    name: 'RB_CURRENTRECORDNUMBER',
    description: 'Returns the current record number being processed within a ForEach loop (1-based)',
    category: 'Runbook'
  },
  {
    name: 'RB_TOTALNUMBEROFRECORDS',
    description: 'Returns the total number of records being processed by the parent ForEach step',
    category: 'Runbook'
  },
  {
    name: 'RB_RECORDCOUNT',
    description: 'Returns the number of records processed by a specific step within the runbook',
    category: 'Runbook'
  }
];

/**
 * WorkflowAutocomplete - Intelligent autocomplete for workflow step references
 *
 * Triggers on {{ and shows:
 * 1. Available steps (filtered by stepOrder)
 * 2. After selecting step + typing _, shows available fields
 * 3. On FX_ shows available functions
 *
 * Notation: {{StepId_FieldName}}
 * Handles forEach steps by resolving to their dataStepId
 */
const WorkflowAutocomplete = React.forwardRef(({
  value,
  onChange,
  allNodes,
  currentNode,
  edges,
  runbookVariables,
  placeholder,
  className,
  ...otherProps
}, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [autocompleteMode, setAutocompleteMode] = useState(null); // 'steps', 'fields', or 'functions'
  const [currentStepId, setCurrentStepId] = useState(null);
  const [parameterHint, setParameterHint] = useState(null); // Show parameter hints for functions
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const internalRef = useRef(null);
  const inputRef = ref || internalRef;
  const suggestionsRef = useRef(null);

  /**
   * Calculate cursor position for dropdown placement
   */
  const calculateCursorPosition = () => {
    if (!inputRef.current || otherProps.as !== 'textarea') {
      return { top: 0, left: 0 };
    }

    const textarea = inputRef.current;
    const cursorPos = textarea.selectionStart;

    // Create a mirror div to calculate position
    const computed = window.getComputedStyle(textarea);
    const fontSize = computed.getPropertyValue('font-size');
    const fontFamily = computed.getPropertyValue('font-family');
    const lineHeight = computed.getPropertyValue('line-height');
    const paddingLeft = parseInt(computed.getPropertyValue('padding-left'));
    const paddingTop = parseInt(computed.getPropertyValue('padding-top'));

    // Create temporary div to measure text
    const mirror = document.createElement('div');
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';
    mirror.style.fontSize = fontSize;
    mirror.style.fontFamily = fontFamily;
    mirror.style.lineHeight = lineHeight;
    mirror.style.width = textarea.clientWidth + 'px';
    mirror.style.overflowWrap = 'break-word';

    document.body.appendChild(mirror);

    // Get text up to cursor
    const textUpToCursor = value.substring(0, cursorPos);
    mirror.textContent = textUpToCursor;

    // Add a span at the end to get the exact position
    const span = document.createElement('span');
    span.textContent = '|';
    mirror.appendChild(span);

    // Get the height of the mirror content
    const mirrorHeight = mirror.offsetHeight;

    document.body.removeChild(mirror);

    // Calculate position relative to textarea
    // Use a simple line-based calculation
    const lines = textUpToCursor.split('\n');
    const currentLine = lines.length - 1;
    const lineHeightPx = parseInt(lineHeight) || parseInt(fontSize) * 1.2;

    const top = paddingTop + (currentLine * lineHeightPx) + lineHeightPx - textarea.scrollTop;
    const left = paddingLeft;

    return { top, left };
  };

  /**
   * Resolve a step to its data source
   * If it's a forEach, follow dataStepId chain
   * If it's a lookupTableCache, follow: lookupTableCache -> buildTableCache -> query
   */
  const resolveStepToDataSource = (stepId, edges) => {
    if (!allNodes) return null;

    const step = allNodes.find(n => (n.data.stepId || n.id) === stepId);
    if (!step) return null;

    // If it's a forEach, resolve to its data step
    if (step.data.stepType === 'forEach') {
      let dataStepId = step.data.dataStepId;

      // Auto-detect if not manually set
      if (!dataStepId && edges) {
        const incomingEdges = edges.filter(e => e.target === step.id);
        if (incomingEdges.length > 0) {
          const sourceNode = allNodes.find(n => n.id === incomingEdges[0].source);
          if (sourceNode &&
              (sourceNode.data.stepType === 'query' ||
               sourceNode.data.stepType === 'queryTableCache')) {
            dataStepId = sourceNode.data.stepId || sourceNode.id;
          }
        }
      }

      if (dataStepId) {
        return resolveStepToDataSource(dataStepId, edges);
      }
    }

    // If it's a lookupTableCache, follow the chain to get fields from the underlying query
    // lookupTableCache -> buildTableCache -> query
    if (step.data.stepType === 'lookupTableCache') {
      const buildTableCacheStepId = step.data.dataStepId;
      if (buildTableCacheStepId) {
        const buildTableCacheStep = allNodes.find(n => (n.data.stepId || n.id) === buildTableCacheStepId);
        if (buildTableCacheStep && buildTableCacheStep.data.stepType === 'buildTableCache') {
          const queryStepId = buildTableCacheStep.data.dataStepId;
          if (queryStepId) {
            const queryStep = allNodes.find(n => (n.data.stepId || n.id) === queryStepId);
            if (queryStep && queryStep.data.stepType === 'query') {
              return queryStep;
            }
          }
        }
      }
    }

    return step;
  };

  /**
   * Extract field names from a query/lookup step
   */
  const getFieldsFromStep = (stepId) => {
    // Find the original step (not the resolved one)
    const originalStep = allNodes?.find(n => (n.data.stepId || n.id) === stepId);
    if (!originalStep) return [];

    // Only allow referenceable step types for the ORIGINAL step
    const referenceableStepTypes = ['forEach', 'declareVar', 'lookup', 'create', 'update', 'lookupTableCache'];

    // Check if the original step is directly a query - if so, don't show fields
    if (!referenceableStepTypes.includes(originalStep.data.stepType)) {
      return [];
    }

    // Now resolve to get the actual data source (for forEach steps)
    const dataSource = resolveStepToDataSource(stepId, edges);
    if (!dataSource) return [];

    const fields = [];

    // For query/lookup steps, parse fieldsToQuery
    if (dataSource.data.fieldsToQuery) {
      const fieldList = dataSource.data.fieldsToQuery
        .split(',')
        .map(f => f.trim())
        .filter(f => f.length > 0);
      fields.push(...fieldList);
    }

    return fields;
  };

  /**
   * Get available steps (those defined before current step)
   */
  const getAvailableSteps = () => {
    if (!allNodes || !currentNode) return [];

    // Only these step types can be referenced
    const referenceableStepTypes = ['forEach', 'declareVar', 'lookup', 'create', 'update', 'lookupTableCache'];

    return allNodes
      .filter(n => {
        // Must not be start or group node
        if (n.id === 'start' || n.type === 'groupNode') return false;

        // Don't show current node
        if (n.id === currentNode.id) return false;

        // Only show referenceable step types (exclude query, etc.)
        if (!referenceableStepTypes.includes(n.data.stepType)) return false;

        // Only show steps before this one
        if (n.data.stepOrder != null && currentNode.data.stepOrder != null) {
          return n.data.stepOrder < currentNode.data.stepOrder;
        }

        return true;
      })
      .map(n => ({
        id: n.data.stepId || n.id,
        label: n.data.label,
        stepType: n.data.stepType,
        description: n.data.description,
        displayText: `${n.data.stepId || n.id} - ${n.data.label}${n.data.description ? ` (${n.data.description})` : ''}`,
      }));
  };

  /**
   * Note: Variables are not shown in {{ autocomplete - they should only be accessed
   * via FX lookup functions (e.g., FX_LOOKUPVALUEBYKEY(V1, "key")).
   */

  /**
   * Detect what's being typed and show appropriate suggestions
   */
  useEffect(() => {
    if (!inputRef.current) return;

    const cursorPos = inputRef.current.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);

    // Check for function autocomplete (FX_)
    let insideFunction = false;
    const lastFxIndex = textBeforeCursor.lastIndexOf('FX_');
    if (lastFxIndex !== -1) {
      // Check if we're still typing the function name (no opening parenthesis yet, or cursor is before it)
      const textAfterFx = textBeforeCursor.substring(lastFxIndex);
      const openParenIndex = textAfterFx.indexOf('(');
      const closeParenIndex = textAfterFx.indexOf(')');

      // Check if we're inside function parentheses to show parameter hints
      if (openParenIndex !== -1 && (closeParenIndex === -1 || cursorPos < lastFxIndex + closeParenIndex)) {
        const functionName = textAfterFx.substring(0, openParenIndex);
        const matchedFunction = WORKFLOW_FUNCTIONS.find(fn => fn.name === functionName);

        if (matchedFunction) {
          insideFunction = true;
          // Show parameter hint
          setParameterHint({
            signature: matchedFunction.signature,
            description: matchedFunction.description
          });

          // Check if this is a lookup function and we're on the first parameter
          const isLookupFunction = functionName.startsWith('FX_LOOKUPVALUE');
          if (isLookupFunction) {
            // Get the text inside the parentheses
            const insideParens = textAfterFx.substring(openParenIndex + 1);
            const commaIndex = insideParens.indexOf(',');

            // If there's no comma yet or cursor is before the comma, we're on the first parameter
            const cursorInParens = cursorPos - (lastFxIndex + openParenIndex + 1);
            if (commaIndex === -1 || cursorInParens <= commaIndex) {
              // Show variable suggestions for the first parameter
              const typedParam = insideParens.substring(0, commaIndex === -1 ? insideParens.length : commaIndex).trim();

              if (runbookVariables && runbookVariables.length > 0) {
                const variableSuggestions = runbookVariables
                  .filter(v => v.variableNumber.toLowerCase().includes(typedParam.toLowerCase()))
                  .map(v => ({
                    value: v.variableNumber,
                    display: `${v.variableNumber} - ${v.variableName}`,
                    type: 'variableNumber',
                    description: `Variable: ${v.variableName}`
                  }));

                if (variableSuggestions.length > 0) {
                  setSuggestions(variableSuggestions);
                  setAutocompleteMode('variableNumber');
                  setShowSuggestions(true);
                  setSelectedIndex(0);
                  setCursorPosition(cursorPos);
                  setDropdownPosition(calculateCursorPosition());
                  return;
                }
              }
            }
          }

          // Don't return here - allow step/field autocomplete to work inside function parameters
        }
      } else {
        // Clear parameter hint when not inside function
        setParameterHint(null);
      }

      if (openParenIndex === -1 || cursorPos <= lastFxIndex + openParenIndex) {
        // Still typing function name
        const typedFunction = textAfterFx.split(/[\s(]/)[0]; // Get text up to space or (
        const filtered = WORKFLOW_FUNCTIONS.filter(fn =>
          fn.name.toLowerCase().startsWith(typedFunction.toLowerCase())
        );

        if (filtered.length > 0) {
          setSuggestions(filtered.map(fn => ({
            value: fn.name,
            display: fn.signature,
            description: fn.description,
            type: 'function'
          })));
          setAutocompleteMode('functions');
          setShowSuggestions(true);
          setSelectedIndex(0);
          setParameterHint(null);
          setCursorPosition(cursorPos);
          setDropdownPosition(calculateCursorPosition());
          return;
        }
      }
    } else {
      // Clear parameter hint when not in a function
      setParameterHint(null);
    }

    // Find the last {{ before cursor
    const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');

    if (lastOpenBrace === -1) {
      setShowSuggestions(false);
      return;
    }

    // Check if there's a closing }} after the {{
    const textAfterOpenBrace = textBeforeCursor.substring(lastOpenBrace);
    const hasClosingBrace = textAfterOpenBrace.indexOf('}}') !== -1;

    if (hasClosingBrace) {
      setShowSuggestions(false);
      return;
    }

    // Extract what's been typed after {{
    const typedAfterBrace = textBeforeCursor.substring(lastOpenBrace + 2);

    // Check if there's an underscore (field reference)
    const underscoreIndex = typedAfterBrace.indexOf('_');

    if (underscoreIndex === -1) {
      // No underscore yet - show step suggestions and runbook variables
      const availableSteps = getAvailableSteps();

      const filteredSteps = availableSteps.filter(step =>
        step.id.toLowerCase().includes(typedAfterBrace.toLowerCase()) ||
        step.label.toLowerCase().includes(typedAfterBrace.toLowerCase())
      );

      // Filter runbook variables (RB_) based on what's typed
      const filteredRunbookVars = RUNBOOK_VARIABLES.filter(rv =>
        rv.name.toLowerCase().includes(typedAfterBrace.toLowerCase())
      );

      // Combine steps and runbook variables
      const allSuggestions = [
        ...filteredSteps.map(s => ({ value: s.id, display: s.displayText, type: 'step' })),
        ...filteredRunbookVars.map(rv => ({
          value: rv.name,
          display: rv.name,
          description: rv.description,
          type: 'runbookVariable'
        }))
      ];

      setSuggestions(allSuggestions);
      setAutocompleteMode('steps');
      const hasSuggestions = allSuggestions.length > 0;
      setShowSuggestions(hasSuggestions);
      setSelectedIndex(0);
      if (hasSuggestions) {
        setDropdownPosition(calculateCursorPosition());
      }
    } else {
      // Underscore found - show field suggestions (for steps only)
      const stepId = typedAfterBrace.substring(0, underscoreIndex);
      const fieldFilter = typedAfterBrace.substring(underscoreIndex + 1);

      // Note: Variables (e.g., V1) won't show key suggestions since we don't have
      // the actual key-value pairs at design time. Users must type variable keys manually.

      // Get fields - this will automatically resolve forEach to its dataStepId
      const fields = getFieldsFromStep(stepId);
      const filtered = fields.filter(field =>
        field.toLowerCase().includes(fieldFilter.toLowerCase())
      );

      setSuggestions(filtered.map(f => ({ value: f, display: f, type: 'field' })));
      setAutocompleteMode('fields');
      setCurrentStepId(stepId);
      const hasFieldSuggestions = filtered.length > 0;
      setShowSuggestions(hasFieldSuggestions);
      setSelectedIndex(0);
      if (hasFieldSuggestions) {
        setDropdownPosition(calculateCursorPosition());
      }
    }

    setCursorPosition(cursorPos);
  }, [value, allNodes, currentNode]);

  /**
   * Insert suggestion at cursor position
   */
  const insertSuggestion = (suggestion) => {
    if (!inputRef.current) return;

    const cursorPos = inputRef.current.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const textAfterCursor = value.substring(cursorPos);

    let newValue;
    let newCursorPos;

    if (autocompleteMode === 'functions') {
      // Replace FX_ with the full function name and add opening parenthesis
      const lastFxIndex = textBeforeCursor.lastIndexOf('FX_');
      const textBeforeFx = textBeforeCursor.substring(0, lastFxIndex);

      newValue = textBeforeFx + suggestion.value + '()' + textAfterCursor;
      newCursorPos = textBeforeFx.length + suggestion.value.length + 1; // Position cursor inside parentheses
    } else if (autocompleteMode === 'variableNumber') {
      // Replace the text being typed in the function parameter with the variable number
      const lastFxIndex = textBeforeCursor.lastIndexOf('FX_');
      const textAfterFx = textBeforeCursor.substring(lastFxIndex);
      const openParenIndex = textAfterFx.indexOf('(');
      const insideParens = textAfterFx.substring(openParenIndex + 1);
      const commaIndex = insideParens.indexOf(',');

      // Find where the current parameter starts
      const textBeforeParam = textBeforeCursor.substring(0, lastFxIndex + openParenIndex + 1);

      // If there's already a comma, preserve everything after it
      let textAfterParam = textAfterCursor;
      if (commaIndex !== -1) {
        textAfterParam = insideParens.substring(commaIndex) + textAfterCursor;
      }

      newValue = textBeforeParam + suggestion.value + textAfterParam;
      newCursorPos = textBeforeParam.length + suggestion.value.length;
    } else {
      // Find the last {{ before cursor
      const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');
      const textBeforeOpenBrace = textBeforeCursor.substring(0, lastOpenBrace + 2);

      if (suggestion.type === 'runbookVariable') {
        // Runbook variables insert complete with closing }}
        newValue = textBeforeOpenBrace + suggestion.value + '}}' + textAfterCursor;
        newCursorPos = textBeforeOpenBrace.length + suggestion.value.length + 2;
      } else if (autocompleteMode === 'steps') {
        // Insert step ID and an underscore
        newValue = textBeforeOpenBrace + suggestion.value + '_' + textAfterCursor;
        newCursorPos = textBeforeOpenBrace.length + suggestion.value.length + 1;
      } else if (autocompleteMode === 'fields') {
        // Replace the part after the underscore with the field name and close with }}
        const textAfterOpenBrace = textBeforeCursor.substring(lastOpenBrace + 2);
        const underscoreIndex = textAfterOpenBrace.indexOf('_');
        const textBeforeUnderscore = textBeforeCursor.substring(0, lastOpenBrace + 2 + underscoreIndex + 1);

        newValue = textBeforeUnderscore + suggestion.value + '}}' + textAfterCursor;
        newCursorPos = textBeforeUnderscore.length + suggestion.value.length + 2;
      }
    }

    onChange({ target: { value: newValue } });

    // Set cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = newCursorPos;
        inputRef.current.selectionEnd = newCursorPos;
        inputRef.current.focus();

        // For step insertion, manually trigger field suggestions
        // Note: Variables won't show key suggestions since we don't have that info at design time
        // Runbook variables are complete and don't need field suggestions
        if (autocompleteMode === 'steps' && suggestion.type === 'step') {
          // Get fields for the selected step
          const fields = getFieldsFromStep(suggestion.value);
          if (fields.length > 0) {
            setSuggestions(fields.map(f => ({ value: f, display: f, type: 'field' })));
            setAutocompleteMode('fields');
            setCurrentStepId(suggestion.value);
            setShowSuggestions(true);
            setSelectedIndex(0);
          } else {
            setShowSuggestions(false);
          }
        } else {
          // Runbook variables and other types close suggestions
          setShowSuggestions(false);
        }
      }
    }, 0);
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
      case 'Tab':
        if (suggestions.length > 0) {
          e.preventDefault();
          insertSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  /**
   * Scroll selected item into view
   */
  useEffect(() => {
    if (suggestionsRef.current && showSuggestions) {
      const selectedItem = suggestionsRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, showSuggestions]);

  // Determine if we should render as textarea or input
  const InputComponent = otherProps.as === 'textarea' ? 'textarea' : 'input';
  const inputType = otherProps.as === 'textarea' ? undefined : 'text';

  return (
    <div className="workflow-autocomplete-wrapper" style={{ position: 'relative' }}>
      <InputComponent
        ref={inputRef}
        type={inputType}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        {...otherProps}
      />

      {/* Parameter Hint Tooltip */}
      {parameterHint && (
        <div className="workflow-parameter-hint" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '0.85rem',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
            {parameterHint.signature}
          </div>
          <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>
            {parameterHint.description}
          </div>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div
          className="workflow-autocomplete-suggestions"
          style={{
            top: otherProps.as === 'textarea' ? `${dropdownPosition.top}px` : undefined,
            left: otherProps.as === 'textarea' ? `${dropdownPosition.left}px` : undefined
          }}
        >
          <ListGroup ref={suggestionsRef}>
            {suggestions.map((suggestion, index) => (
              <ListGroup.Item
                key={index}
                active={index === selectedIndex}
                onClick={() => insertSuggestion(suggestion)}
                className="workflow-autocomplete-item"
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <div>{suggestion.display}</div>
                  {suggestion.type === 'function' && suggestion.description && (
                    <small className="text-muted d-block">{suggestion.description}</small>
                  )}
                  {suggestion.type === 'runbookVariable' && suggestion.description && (
                    <small className="text-muted d-block">{suggestion.description}</small>
                  )}
                </div>
                {suggestion.type === 'step' && (
                  <small className="ms-2 text-muted">Step</small>
                )}
                {suggestion.type === 'variable' && (
                  <small className="ms-2 text-muted">Variable</small>
                )}
                {suggestion.type === 'variableNumber' && (
                  <small className="ms-2 text-muted">Variable</small>
                )}
                {suggestion.type === 'field' && (
                  <small className="ms-2 text-muted">Field</small>
                )}
                {suggestion.type === 'variableKey' && (
                  <small className="ms-2 text-muted">Variable Key</small>
                )}
                {suggestion.type === 'function' && (
                  <small className="ms-2 text-muted">Function</small>
                )}
                {suggestion.type === 'runbookVariable' && (
                  <small className="ms-2 text-muted">Runbook</small>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
});

WorkflowAutocomplete.displayName = 'WorkflowAutocomplete';

export default WorkflowAutocomplete;
